import {
  ALLOWED_PLATFORM_IDS,
  COUPON_CATEGORIES,
  COUPON_USAGE_OPERATIONS,
  COUPON_VALIDATION,
  DISCOUNT_TYPES,
} from "constants/coupons";
import { parsePackageIds } from "helpers/packageIds";
import { normalizeDate } from "utils/dateUtils";

const COUPON_CODE_REGEX = new RegExp(COUPON_VALIDATION.COUPON_CODE_REGEX);

const hasMaxDecimalPlaces = (value, maxPlaces) => {
  if (value === "" || value === null || value === undefined) return true;
  const str = String(value).trim();
  const decimalIndex = str.indexOf(".");
  if (decimalIndex === -1) return true;
  return str.length - decimalIndex - 1 <= maxPlaces;
};

const decimalPlacesError = (label) =>
  `${label} can have at most ${COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES} decimal places.`;

const validateAmountDecimalPlaces = (value, fieldName, label, errors) => {
  if (value === "" || value === null || value === undefined) return;
  if (!hasMaxDecimalPlaces(value, COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES)) {
    errors[fieldName] = decimalPlacesError(label);
  }
};

const parseCommaSeparatedIds = (value) => parsePackageIds(value);

export const parseCouponCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : NaN;
};

export const getCurrentTotalCouponCount = (values) => {
  const parsed = parseCouponCount(
    values.totalCouponCount ?? values.totalUsageLimit ?? 0
  );

  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export const getMaxIncreaseCount = (values) =>
  Math.max(
    COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT -
      getCurrentTotalCouponCount(values),
    0
  );

export const getUsageCountChangeMaxError = (values, changeCount) => {
  const count = parseCouponCount(changeCount);
  if (!Number.isFinite(count)) return null;

  const currentTotal = getCurrentTotalCouponCount(values);

  if (values.usageOperation === COUPON_USAGE_OPERATIONS.DECREASE) {
    if (count > currentTotal) {
      return `Cannot decrease by more than the current total (${currentTotal.toLocaleString()}).`;
    }

    return null;
  }

  if (values.usageOperation === COUPON_USAGE_OPERATIONS.INCREASE) {
    const projectedTotal = currentTotal + count;

    if (projectedTotal > COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT) {
      const maxIncrease = getMaxIncreaseCount(values);
      return `New total would be ${projectedTotal.toLocaleString()}, which exceeds the maximum of ${COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT.toLocaleString()}. You can increase by at most ${maxIncrease.toLocaleString()}.`;
    }
  }

  return null;
};

export const validateCreateCouponForm = (values, now = new Date()) => {
  const errors = {};
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const code = (values.code ?? "").trim();
  if (!code) {
    errors.code = "Coupon code is required.";
  } else if (/\s/.test(code)) {
    errors.code = "Coupon code cannot contain whitespace.";
  } else if (code.length > COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH) {
    errors.code = `Coupon code cannot exceed ${COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH} characters.`;
  } else if (!COUPON_CODE_REGEX.test(code)) {
    errors.code =
      "Coupon code must start with a letter or number and may contain only letters, digits, hyphen, and underscore.";
  }

  const category = (values.category ?? "").trim();
  if (!category) {
    errors.category = "Coupon category is required.";
  } else if (!COUPON_CATEGORIES.includes(category)) {
    errors.category = `Category must be one of: ${COUPON_CATEGORIES.join(", ")}.`;
  }

  const remarks = (values.remarks ?? "").trim();
  if (!remarks) {
    errors.remarks = "Remarks are required.";
  } else if (remarks.length > COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH) {
    errors.remarks = `Remarks cannot exceed ${COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH} characters.`;
  }

  const effectiveDate = normalizeDate(values.effectiveDate);
  const expiryDate = normalizeDate(values.expiryDate);

  if (!effectiveDate) {
    errors.effectiveDate = "Effective date is required.";
  } else if (effectiveDate < today) {
    errors.effectiveDate = "Effective date must be today or a future date.";
  } else {
    const maxEffective = new Date(today);
    maxEffective.setDate(
      maxEffective.getDate() +
        COUPON_VALIDATION.MAX_ALLOWED_EFFECTIVE_DATE_OFFSET_DAYS
    );
    if (effectiveDate > maxEffective) {
      errors.effectiveDate = `Effective date cannot be more than ${COUPON_VALIDATION.MAX_ALLOWED_EFFECTIVE_DATE_OFFSET_DAYS} days from today.`;
    }
  }

  if (!expiryDate) {
    errors.expiryDate = "Expiry date is required.";
  } else if (effectiveDate) {
    const minExpiry = new Date(effectiveDate);
    minExpiry.setDate(
      minExpiry.getDate() + COUPON_VALIDATION.MIN_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS
    );
    const maxExpiry = new Date(effectiveDate);
    maxExpiry.setDate(
      maxExpiry.getDate() +
        COUPON_VALIDATION.MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS
    );

    if (expiryDate < minExpiry) {
      errors.expiryDate = `Expiry date must be at least ${COUPON_VALIDATION.MIN_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS} day after the effective date.`;
    } else if (expiryDate > maxExpiry) {
      errors.expiryDate = `Expiry date cannot be more than ${COUPON_VALIDATION.MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS} days after the effective date.`;
    }
  }

  const discountType = values.discountType;
  if (!discountType) {
    errors.discountType = "Discount type is required.";
  } else if (
    discountType !== DISCOUNT_TYPES.FLAT &&
    discountType !== DISCOUNT_TYPES.PERCENTAGE
  ) {
    errors.discountType = "Discount type must be FLAT or PERCENTAGE.";
  }

  const discountValue = Number(values.discountValue);
  if (
    values.discountValue === "" ||
    values.discountValue === null ||
    values.discountValue === undefined ||
    Number.isNaN(discountValue)
  ) {
    errors.discountValue = "Discount value is required.";
  } else if (
    !hasMaxDecimalPlaces(
      values.discountValue,
      COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES
    )
  ) {
    errors.discountValue = decimalPlacesError("Discount value");
  } else if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
    if (
      discountValue <= COUPON_VALIDATION.MIN_PERCENTAGE_DISCOUNT ||
      discountValue > COUPON_VALIDATION.MAX_PERCENTAGE_DISCOUNT
    ) {
      errors.discountValue =
        "Percentage discount must be greater than 0 and up to 100.";
    }
  } else if (discountType === DISCOUNT_TYPES.FLAT) {
    if (
      discountValue <= COUPON_VALIDATION.MIN_FLAT_DISCOUNT_AMOUNT ||
      discountValue > COUPON_VALIDATION.MAX_FLAT_DISCOUNT_AMOUNT
    ) {
      errors.discountValue =
        "Flat discount must be greater than 0 and up to 100000.";
    }
  }

  const maxAllowedAmount = values.maxAllowedAmount;
  if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
    const maxAmount = Number(maxAllowedAmount);
    if (
      maxAllowedAmount === "" ||
      maxAllowedAmount === null ||
      maxAllowedAmount === undefined ||
      Number.isNaN(maxAmount)
    ) {
      errors.maxAllowedAmount =
        "Max allowed amount is required for percentage discounts.";
    } else if (
      !hasMaxDecimalPlaces(
        maxAllowedAmount,
        COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES
      )
    ) {
      errors.maxAllowedAmount = decimalPlacesError("Max allowed amount");
    } else if (maxAmount < 0) {
      errors.maxAllowedAmount = "Max allowed amount cannot be negative.";
    }
  } else if (
    maxAllowedAmount !== "" &&
    maxAllowedAmount !== null &&
    maxAllowedAmount !== undefined
  ) {
    errors.maxAllowedAmount =
      "Max allowed amount must not be provided for flat discounts.";
  }

  const totalCouponCount = Number(values.totalCouponCount);
  if (
    values.totalCouponCount === "" ||
    values.totalCouponCount === null ||
    values.totalCouponCount === undefined ||
    Number.isNaN(totalCouponCount)
  ) {
    errors.totalCouponCount = "Total coupon count is required.";
  } else if (!Number.isInteger(totalCouponCount) || totalCouponCount <= 0) {
    errors.totalCouponCount = "Coupon count must be greater than 0.";
  } else if (totalCouponCount > COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT) {
    errors.totalCouponCount = `Coupon count cannot exceed ${COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT}.`;
  }

  if (
    values.isApplicableMultipleTimes === null ||
    values.isApplicableMultipleTimes === undefined
  ) {
    errors.isApplicableMultipleTimes =
      "Please specify whether the coupon can be applied multiple times.";
  }

  if (
    values.isApplicableForSingleUser === null ||
    values.isApplicableForSingleUser === undefined
  ) {
    errors.isApplicableForSingleUser =
      "Please specify whether the coupon is for a single user.";
  }

  if (values.isApplicableForSingleUser === true) {
    const uamId = Number(values.uamId);
    if (
      values.uamId === "" ||
      values.uamId === null ||
      values.uamId === undefined ||
      Number.isNaN(uamId)
    ) {
      errors.uamId = "UAM ID is required when the coupon is for a single user.";
    } else if (!Number.isInteger(uamId)) {
      errors.uamId = "UAM ID must be a whole number.";
    }
  } else if (
    values.uamId !== "" &&
    values.uamId !== null &&
    values.uamId !== undefined
  ) {
    errors.uamId =
      "UAM ID must not be provided when the coupon is not restricted to a single user.";
  }

  const minimumPackageMrp = Number(values.minimumPackageMrp);
  if (
    values.minimumPackageMrp === "" ||
    values.minimumPackageMrp === null ||
    values.minimumPackageMrp === undefined ||
    Number.isNaN(minimumPackageMrp)
  ) {
    errors.minimumPackageMrp = "Minimum package MRP is required.";
  } else if (minimumPackageMrp < COUPON_VALIDATION.MIN_MRP_AMOUNT) {
    errors.minimumPackageMrp = "Minimum package MRP cannot be negative.";
  } else {
    validateAmountDecimalPlaces(
      values.minimumPackageMrp,
      "minimumPackageMrp",
      "Minimum package MRP",
      errors
    );
  }

  if (
    values.minimumCartMrp !== "" &&
    values.minimumCartMrp !== null &&
    values.minimumCartMrp !== undefined
  ) {
    const minimumCartMrp = Number(values.minimumCartMrp);
    if (Number.isNaN(minimumCartMrp)) {
      errors.minimumCartMrp = "Minimum cart MRP must be a valid number.";
    } else if (minimumCartMrp < COUPON_VALIDATION.MIN_MRP_AMOUNT) {
      errors.minimumCartMrp = "Minimum cart MRP cannot be negative.";
    } else {
      validateAmountDecimalPlaces(
        values.minimumCartMrp,
        "minimumCartMrp",
        "Minimum cart MRP",
        errors
      );
    }
  }

  if (
    values.selectAllPackages === null ||
    values.selectAllPackages === undefined
  ) {
    errors.selectAllPackages = "Please specify package applicability.";
  } else if (values.selectAllPackages === true) {
    const packageIds = parseCommaSeparatedIds(values.packageIds);
    if (packageIds.length > 0) {
      errors.packageIds =
        "Package IDs must be empty when all packages are selected.";
    }
  } else {
    const packageIds = parseCommaSeparatedIds(values.packageIds);
    if (packageIds.length === 0) {
      errors.packageIds =
        "Select at least one package when all packages is not selected.";
    } else if (
      packageIds.includes(COUPON_VALIDATION.ALL_PACKAGES_ID)
    ) {
      errors.packageIds = "Reserved all-packages ID (0) is not allowed.";
    }
  }

  if (
    values.selectAllPlatforms === null ||
    values.selectAllPlatforms === undefined
  ) {
    errors.selectAllPlatforms = "Please specify platform applicability.";
  } else if (values.selectAllPlatforms === true) {
    const platformIds = parseCommaSeparatedIds(values.platformIds);
    if (platformIds.length > 0) {
      errors.platformIds =
        "Platform IDs must be empty when all platforms are selected.";
    }
  } else {
    const platformIds = parseCommaSeparatedIds(values.platformIds);
    if (platformIds.length === 0) {
      errors.platformIds =
        "Select at least one platform when all platforms is not selected.";
    } else if (
      platformIds.some((id) => !ALLOWED_PLATFORM_IDS.includes(id))
    ) {
      errors.platformIds = "Only Web, Android, and iOS platforms are allowed.";
    } else if (
      platformIds.includes(COUPON_VALIDATION.ALL_PLATFORMS_ID)
    ) {
      errors.platformIds = "Reserved all-platforms ID (1) is not allowed.";
    }
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};

export const validateEditCouponForm = (values) => {
  const errors = {};

  if (!values.expiryDate) {
    errors.expiryDate = "Expiry date is required.";
  }

  const remarks = (values.remarks ?? "").trim();
  if (!remarks) {
    errors.remarks = "Remarks are required.";
  } else if (remarks.length > COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH) {
    errors.remarks = `Remarks cannot exceed ${COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH} characters.`;
  }

  if (values.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    const maxAllowed = values.maxAllowedAmount;
    if (
      maxAllowed === "" ||
      maxAllowed === null ||
      maxAllowed === undefined
    ) {
      errors.maxAllowedAmount =
        "Max allowed amount is required for percentage discounts.";
    } else if (Number(maxAllowed) < 0) {
      errors.maxAllowedAmount = "Max allowed amount cannot be negative.";
    } else {
      validateAmountDecimalPlaces(
        maxAllowed,
        "maxAllowedAmount",
        "Max allowed amount",
        errors
      );
    }
  }

  if (!values.usageOperation) {
    errors.usageOperation = "Select increase or decrease.";
  }

  const usageCount = values.usageCountChange;
  if (
    usageCount === "" ||
    usageCount === null ||
    usageCount === undefined
  ) {
    errors.usageCountChange = "Enter the count to increase or decrease.";
  } else {
    const count = parseCouponCount(usageCount);
    if (!Number.isInteger(count) || count <= 0) {
      errors.usageCountChange = "Count must be greater than 0.";
    } else {
      const maxError = getUsageCountChangeMaxError(values, count);
      if (maxError) {
        errors.usageCountChange = maxError;
      }
    }
  }

  const minimumPackageMrp = values.minimumPackageMrp;
  if (
    minimumPackageMrp === "" ||
    minimumPackageMrp === null ||
    minimumPackageMrp === undefined
  ) {
    errors.minimumPackageMrp = "Minimum package MRP is required.";
  } else if (Number(minimumPackageMrp) < COUPON_VALIDATION.MIN_MRP_AMOUNT) {
    errors.minimumPackageMrp = `Minimum package MRP must be at least ${COUPON_VALIDATION.MIN_MRP_AMOUNT}.`;
  } else {
    validateAmountDecimalPlaces(
      minimumPackageMrp,
      "minimumPackageMrp",
      "Minimum package MRP",
      errors
    );
  }

  if (
    values.minimumCartMrp !== "" &&
    values.minimumCartMrp !== null &&
    values.minimumCartMrp !== undefined
  ) {
    if (Number.isNaN(Number(values.minimumCartMrp))) {
      errors.minimumCartMrp = "Minimum cart MRP must be a valid number.";
    } else if (Number(values.minimumCartMrp) < 0) {
      errors.minimumCartMrp = "Minimum cart MRP cannot be negative.";
    } else {
      validateAmountDecimalPlaces(
        values.minimumCartMrp,
        "minimumCartMrp",
        "Minimum cart MRP",
        errors
      );
    }
  }

  if (
    values.selectAllPackages === null ||
    values.selectAllPackages === undefined
  ) {
    errors.selectAllPackages = "Please specify package applicability.";
  } else if (values.selectAllPackages === true) {
    const packageIds = parseCommaSeparatedIds(values.packageIds);
    if (packageIds.length > 0) {
      errors.packageIds =
        "Package IDs must be empty when all packages are selected.";
    }
  } else {
    const packageIds = parseCommaSeparatedIds(values.packageIds);
    if (packageIds.length === 0) {
      errors.packageIds =
        "Select at least one package when all packages is not selected.";
    } else if (packageIds.includes(COUPON_VALIDATION.ALL_PACKAGES_ID)) {
      errors.packageIds = "Reserved all-packages ID (0) is not allowed.";
    }
  }

  if (
    values.selectAllPlatforms === null ||
    values.selectAllPlatforms === undefined
  ) {
    errors.selectAllPlatforms = "Please specify platform applicability.";
  } else if (values.selectAllPlatforms === true) {
    const platformIds = parseCommaSeparatedIds(values.platformIds);
    if (platformIds.length > 0) {
      errors.platformIds =
        "Platform IDs must be empty when all platforms are selected.";
    }
  } else {
    const platformIds = parseCommaSeparatedIds(values.platformIds);
    if (platformIds.length === 0) {
      errors.platformIds =
        "Select at least one platform when all platforms is not selected.";
    } else if (
      platformIds.some((id) => !ALLOWED_PLATFORM_IDS.includes(id))
    ) {
      errors.platformIds = "Only Web, Android, and iOS platforms are allowed.";
    } else if (platformIds.includes(COUPON_VALIDATION.ALL_PLATFORMS_ID)) {
      errors.platformIds = "Reserved all-platforms ID (1) is not allowed.";
    }
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};
